import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Volume2, Sliders, Plus, Edit2, Trash2 } from 'lucide-react';

const ToneManager = () => {
  const [tones, setTones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingTone, setEditingTone] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    attributes: { formality: 5, enthusiasm: 5, technicality: 5 },
    examples: ['']
  });

  useEffect(() => {
    fetchTones();
  }, []);

  const fetchTones = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/ai-studio/tones`,
        { credentials: 'include' }
      );
      const data = await response.json();
      if (data.success) {
        setTones(data.tones || []);
      }
    } catch (error) {
      console.error('Error fetching tones:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTone = async (e) => {
    e.preventDefault();
    
    // If editing, use update endpoint
    if (editingTone) {
      return handleSaveEdit(e);
    }
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/ai-studio/tones`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(formData)
        }
      );
      const data = await response.json();
      if (data.success) {
        handleModalClose();
        fetchTones();
      }
    } catch (error) {
      console.error('Error creating tone:', error);
    }
  };

  const handleEditTone = (tone) => {
    setEditingTone(tone);
    setFormData({
      name: tone.name,
      description: tone.description,
      attributes: tone.attributes || { formality: 5, enthusiasm: 5, technicality: 5 },
      examples: tone.examples || ['']
    });
    setShowCreateModal(true);
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/ai-studio/tones/${editingTone.id}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(formData)
        }
      );
      const data = await response.json();
      if (data.success) {
        handleModalClose();
        fetchTones();
      }
    } catch (error) {
      console.error('Error updating tone:', error);
      alert('Erreur lors de la mise à jour du ton');
    }
  };

  const handleDeleteTone = async (toneId, toneName) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer le ton "${toneName}" ?`)) {
      return;
    }
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/ai-studio/tones/${toneId}`,
        {
          method: 'DELETE',
          credentials: 'include'
        }
      );
      const data = await response.json();
      if (data.success) {
        fetchTones();
      } else {
        alert(data.detail || 'Erreur lors de la suppression');
      }
    } catch (error) {
      console.error('Error deleting tone:', error);
      alert('Erreur lors de la suppression du ton');
    }
  };

  const handleModalClose = () => {
    setShowCreateModal(false);
    setEditingTone(null);
    setFormData({ name: '', description: '', attributes: { formality: 5, enthusiasm: 5, technicality: 5 }, examples: [''] });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center">
        <div className="text-slate-400">Chargement des tons...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-8">
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-white mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Volume2 className="inline mr-3 text-cyan-400" size={36} />
              Tone Manager
            </h1>
            <p className="text-slate-400 text-lg">Créez et personnalisez vos styles rédactionnels</p>
          </div>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setShowCreateModal(true)}
            className="px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-cyan-500/30 transition-all"
            data-testid="create-tone-btn"
          >
            <Plus size={20} className="inline mr-2" />
            Nouveau Ton
          </motion.button>
        </div>
      </div>

      {/* Tones Grid */}
      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {tones.map((tone, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-all duration-200"
            data-testid={`tone-${index}`}
          >
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-2">
                <Volume2 className="text-cyan-400" size={20} />
                <h3 className="text-xl font-semibold text-white">{tone.name}</h3>
              </div>
              <p className="text-sm text-slate-400 mb-4">{tone.description}</p>
              
              {/* Attributes */}
              <div className="space-y-2">
                {Object.entries(tone.attributes || {}).map(([key, value]) => (
                  <div key={key} className="flex items-center justify-between text-sm">
                    <span className="text-slate-400 capitalize">{key}</span>
                    <div className="flex items-center gap-2">
                      <div className="w-24 h-2 bg-slate-700 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-cyan-500 to-blue-500"
                          style={{ width: `${(value / 10) * 100}%` }}
                        />
                      </div>
                      <span className="text-cyan-400 font-medium w-8">{value}/10</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-2 mt-4">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => handleEditTone(tone)}
                className="flex-1 px-4 py-2 bg-cyan-500/10 text-cyan-400 rounded-lg border border-cyan-500/30 hover:bg-cyan-500/20 transition-all text-sm"
                data-testid={`edit-tone-${index}`}
              >
                <Edit2 size={16} className="inline mr-2" />
                Modifier
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => handleDeleteTone(tone.id, tone.name)}
                className="px-4 py-2 bg-red-500/10 text-red-400 rounded-lg border border-red-500/30 hover:bg-red-500/20 transition-all"
                data-testid={`delete-tone-${index}`}
              >
                <Trash2 size={16} />
              </motion.button>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50" onClick={handleModalClose}>
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-slate-900 border border-white/10 rounded-2xl p-8 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-2xl font-bold text-white mb-6">
              {editingTone ? 'Modifier le Ton' : 'Créer un Ton'}
            </h2>
            <form onSubmit={handleCreateTone}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Nom du ton</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-800 border border-white/10 rounded-xl text-white focus:outline-none focus:border-cyan-500"
                    required
                    placeholder="Ex: Professionnel, Premium, Inspirant..."
                    data-testid="tone-name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-800 border border-white/10 rounded-xl text-white focus:outline-none focus:border-cyan-500 h-20"
                    required
                    data-testid="tone-description-input"
                  />
                </div>
                
                {/* Attributes Sliders */}
                <div className="space-y-4">
                  <label className="block text-sm font-medium text-slate-300">Attributs</label>
                  {Object.entries(formData.attributes).map(([key, value]) => (
                    <div key={key}>
                      <div className="flex justify-between mb-2">
                        <span className="text-sm text-slate-400 capitalize">{key}</span>
                        <span className="text-sm text-cyan-400 font-medium">{value}/10</span>
                      </div>
                      <input
                        type="range"
                        min="0"
                        max="10"
                        value={value}
                        onChange={(e) => setFormData({
                          ...formData,
                          attributes: { ...formData.attributes, [key]: parseInt(e.target.value) }
                        })}
                        className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer"
                      />
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex gap-4 mt-6">
                <button
                  type="button"
                  onClick={handleModalClose}
                  className="flex-1 px-6 py-3 bg-slate-700 text-white rounded-xl hover:bg-slate-600 transition-all"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-cyan-500/30 transition-all"
                  data-testid="submit-tone-btn"
                >
                  {editingTone ? 'Modifier' : 'Créer'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default ToneManager;